// ============================================================================
// OrgFocusCard — carte personne vue focus incrementale (NAV v3)
// ============================================================================
// Largeur uniforme ; hauteur adaptative au contenu. Masque le nom d'equipe
// grise quand il duplique le libelle du badge d'echelon (normalizeOrgLabel).

import { Link } from 'react-router-dom';
import { GsTooltip, StatusBadge } from '@gs/ui';
import type { OrgNode } from '../../lib/org/hierarchy';
import { optionalJobTitle } from '../../lib/org/derivePersonRole';
import { orgLevelI18nKey } from '../../lib/org/deriveOrgLevel';
import { shouldShowOrgTeamName } from '../../lib/org/normalizeOrgLabel';
import { getInitials } from '../../lib/profile/initials';
import { useTranslation } from '../../i18n';

export type OrgFocusCardVariant = 'manager' | 'focus' | 'report' | 'preview';

/** Largeur unique — corrige l'ecart w-52 / w-40 / w-36 (lot 4, pas de min-w cosmetique). */
const CARD_WIDTH = 'w-44';

function OrgInitials({
  node,
  variant,
}: {
  node: OrgNode;
  variant: OrgFocusCardVariant;
}) {
  const size =
    variant === 'focus'
      ? 'h-12 w-12 text-field'
      : variant === 'preview'
        ? 'h-8 w-8 text-chip'
        : 'h-9 w-9 text-chip';
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200 ${size} ${
        node.is_team_director ? 'ring-2 ring-brand-500/40' : ''
      }`}
    >
      {getInitials(node.full_name, node.email)}
    </span>
  );
}

function PersonOrgLevelBadge({ label }: { label: string }) {
  return (
    <StatusBadge variant="neutral">
      {label}
    </StatusBadge>
  );
}

export function OrgFocusCard({
  node,
  variant,
  directReportsCount,
  isHighlighted,
  showAccentBar,
  showDeepen,
  isDeepenOpen,
  onFocus,
  onDeepen,
}: {
  node: OrgNode;
  variant: OrgFocusCardVariant;
  directReportsCount?: number;
  isHighlighted?: boolean;
  showAccentBar?: boolean;
  showDeepen?: boolean;
  isDeepenOpen?: boolean;
  onFocus?: () => void;
  onDeepen?: () => void;
}) {
  const { t } = useTranslation();
  const displayName = node.full_name ?? node.email;
  const jobTitle = optionalJobTitle(node.job_title);
  const levelLabel = t(orgLevelI18nKey(node.role));
  const showTeamName = shouldShowOrgTeamName(node.team_name, levelLabel);
  const isPreview = variant === 'preview';
  const interactive = !isPreview && onFocus;

  const cardInner = (
    <>
      {directReportsCount != null && directReportsCount > 0 && variant === 'report' ? (
        <span
          aria-hidden="true"
          className="absolute right-2 top-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-chip tabular-nums text-slate-500 dark:bg-slate-700 dark:text-slate-300"
        >
          {directReportsCount}
        </span>
      ) : null}
      <OrgInitials node={node} variant={variant} />
      <GsTooltip content={displayName}>
        {node.href && !isPreview ? (
          <Link
            to={node.href}
            onClick={(e) => e.stopPropagation()}
            className={`block w-full truncate font-medium text-slate-900 underline-offset-2 hover:underline dark:text-slate-100 ${
              variant === 'focus' ? 'text-field' : 'text-chip'
            }`}
          >
            {displayName}
          </Link>
        ) : (
          <span
            tabIndex={interactive ? 0 : undefined}
            className={`w-full truncate font-medium text-slate-900 dark:text-slate-100 ${
              variant === 'focus' ? 'text-field' : 'text-chip'
            }`}
          >
            {displayName}
          </span>
        )}
      </GsTooltip>
      {variant === 'focus' && showAccentBar ? (
        <span aria-hidden="true" className="h-0.5 w-10 rounded-full bg-brand-500" />
      ) : null}
      <div className="flex w-full flex-col items-center gap-1">
        <PersonOrgLevelBadge label={levelLabel} />
        {jobTitle ? (
          <GsTooltip content={jobTitle}>
            <span
              tabIndex={interactive ? 0 : undefined}
              className="w-full truncate text-chip text-slate-400 dark:text-slate-500"
            >
              {jobTitle}
            </span>
          </GsTooltip>
        ) : null}
        {showTeamName ? (
          <span className="w-full truncate text-chip text-slate-400 dark:text-slate-500">
            {node.team_name}
          </span>
        ) : null}
      </div>
    </>
  );

  const ring =
    isHighlighted ? 'ring-2 ring-brand-500 ring-offset-2 ring-offset-transparent' : '';

  if (isPreview) {
    return (
      <div
        className={`relative flex shrink-0 flex-col items-center gap-1 rounded-xl border border-dashed border-slate-300 bg-white p-2.5 text-center shadow-xs dark:border-slate-600 dark:bg-slate-800 ${CARD_WIDTH} ${ring}`}
      >
        {cardInner}
      </div>
    );
  }

  return (
    <div className={`relative shrink-0 rounded-xl transition-shadow duration-200 ${ring}`}>
      <button
        type="button"
        onClick={onFocus}
        aria-label={t('gest:organigramme.node.drillIn', { name: displayName })}
        className={`flex w-full touch-manipulation flex-col items-center gap-1.5 rounded-xl border bg-white p-3 text-center shadow-xs transition-colors duration-200 hover:border-slate-300 dark:bg-slate-800 dark:hover:border-slate-500 ${CARD_WIDTH} ${
          variant === 'focus'
            ? 'border-brand-500 dark:border-brand-500'
            : node.is_team_director
              ? 'border-brand-200 dark:border-brand-900'
              : 'border-slate-200 dark:border-slate-700'
        }`}
      >
        {cardInner}
      </button>
      {showDeepen && onDeepen ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeepen();
          }}
          aria-expanded={isDeepenOpen}
          aria-label={
            isDeepenOpen
              ? t('gest:organigramme.focus.deepenClose', { name: displayName })
              : t('gest:organigramme.focus.deepenPreview', { name: displayName })
          }
          className={`absolute -bottom-2.5 left-1/2 z-10 inline-flex h-5 min-w-5 -translate-x-1/2 touch-manipulation items-center justify-center rounded-full border px-1 text-chip font-bold shadow-xs ${
            isDeepenOpen
              ? 'border-brand-500 bg-brand-500 text-white'
              : 'border-slate-200 bg-white text-slate-600 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-slate-100'
          }`}
        >
          +
        </button>
      ) : null}
    </div>
  );
}
